import * as React from "react";
import FilterContext from "../context/Filter";
import { useQuery } from "react-query";
import produce from "immer";
import { arrayMove } from "@dnd-kit/sortable";

type FilterProviderProps = React.PropsWithChildren<{ id?: string }>;

type FilterOption = {
  id: string;
  title: string;
  slug: string;
};

export type FilterState = {
  id: string;
  title: string;
  slug: string;
  options: FilterOption[];
};

type NewOptionPayload = {
  title: string;
};

type Sortable = {
  id: string;
};

type SortOptionsPayload = {
  active: Sortable;
  over: Sortable;
};

type Action =
  | { type: `HYDRATE`; payload: FilterState }
  | { type: `EDIT_TITLE`; payload: string }
  | { type: `ADD_OPTION`; payload: NewOptionPayload }
  | { type: `SORT_OPTIONS`; payload: SortOptionsPayload };

const initialState: FilterState = {
  id: "",
  slug: "",
  title: "",
  options: [],
};

const toSlug = (str: string) => {
  str = str.replace(/^\s+|\s+$/g, ""); // trim
  str = str.toLowerCase();

  // remove accents, swap ñ for n, etc
  var from = "àáäâèéëêìíïîòóöôùúüûñçěščřžýúůďťň·/_,:;";
  var to = "aaaaeeeeiiiioooouuuuncescrzyuudtn------";

  for (var i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
  }

  str = str
    .replace(".", "-") // replace a dot by a dash
    .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
    .replace(/\s+/g, "-") // collapse whitespace and replace by a dash
    .replace(/-+/g, "-") // collapse dashes
    .replace(/\//g, ""); // collapse all forward-slashes

  return str;
};

const reducer = (state: FilterState, action: Action) => {
  switch (action.type) {
    case "HYDRATE": {
      return produce(state, (next) => {
        next.id = action.payload.id;
        next.title = action.payload.title;
        next.slug = action.payload.slug;
        next.options = action.payload.options || [];
      });
    }

    case "EDIT_TITLE": {
      return produce(state, (next) => {
        next.title = action.payload;
      });
    }

    case "ADD_OPTION": {
      if (action.payload.title === "") return state;

      return produce(state, (next) => {
        next.options.push({
          id: action.payload.title,
          title: action.payload.title,
          slug: toSlug(action.payload.title),
        });
      });
    }

    case "SORT_OPTIONS": {
      const { active, over } = action.payload;

      if (active.id !== over.id) {
        const oldIndex = state.options.findIndex(
          (option) => option.id === active.id
        );
        const newIndex = state.options.findIndex(
          (option) => option.id === over.id
        );
        const options = arrayMove(state.options, oldIndex, newIndex);

        return {
          ...state,
          options,
        };
      }

      return state;
    }

    default: {
      return state;
    }
  }
};

function dispatchMiddleware(dispatch) {
  return (action) => {
    switch (action.type) {
      case "ADD_OPTION":
      default:
        return dispatch(action);
    }
  };
}

const FilterProvider = (props: FilterProviderProps) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const decoratedDispatch = dispatchMiddleware(dispatch);

  const { isLoading, error, data } = useQuery(
    ["filters", props.id],
    () => {
      return fetch(
        (window as any).vimeographyApiSettings.root +
          `vimeography/v1/filters/${props.id}`
      ).then((res) => {
        return res.json();
      });
    },
    {
      staleTime: Infinity,
    }
  );

  React.useEffect(() => {
    if (!data) return;
    dispatch({ payload: data, type: "HYDRATE" });
  }, [data]);

  React.useEffect(() => {
    console.log(`save options`);
    console.log(state);
  }, [state.options]);

  return (
    <FilterContext.Provider
      value={{
        isLoading,
        error,
        data,
        state,
        dispatch: decoratedDispatch,
      }}
    >
      {props.children}
    </FilterContext.Provider>
  );
};

export default FilterProvider;
